import React, {
  useCallback, useContext, useMemo,
} from 'react';
import { Button } from 'anf-core-react';
import ShownIn from '../../../components/Swatches/ShownIn';
import SwatchGroup from '../../../components/Swatches/SwatchGroup';
import SizeTileSection from '../../../components/SizeTileSection/SizeTileSection';
import BuyGridAccordionWrapper from '../../../features/BuyGridAccordions/BuyGridAccordionWrapper';
import SizeMessaging from '../../../components/SizeMessaging/SizeMessaging';
import ProductPageContext from '../../../contexts/ProductPageContext';
import BreakpointContext from '../../../contexts/BreakpointContext';
import BrowserContext from '../../../contexts/BrowserContext';
import FinalSale from '../../../components/FinalSale/FinalSale';
import ESpotContent from '../../../components/ESpotContent/ESpotContent';
import ModelMeasurement from '../../../components/ModelMeasurement/ModelMeasurement';
import ContentError from '../../../components/ContentError/ContentError';
import HorizontalLineSeparator from '../../../components/HorizontalSeparator/HorizontalSeparator';
import ExchangesQuickviewNameAndDescription from '../../../components/ExchangesQuickviewNameAndDescription/ExchangesQuickviewNameAndDescription';
import { hasPrimaryOrSecondarySelected } from '../../../helpers/useAddToBagForm';
import useAddToBagErrorHandler from '../../../helpers/useAddToBagFormErrorHandler';

export default function ExchangesQuickviewAddToBagForm({
  swatchChanged,
  categoryId,
  handlePrimarySizeUpdate,
  handleSecondarySizeUpdate,
  reviewData,
  shippingContent,
  eSpotContent,
  setSizeGuideOpen,
  siteRegion,
  preselectExchangeProductId,
  handleCloseModal,
  onModalPrimaryActionSuccessCallback,
  orderLineKeyId,
}) {
  const ProductPageState = useContext(ProductPageContext);
  const { collectionId } = ProductPageState;
  const isBrowser = useContext(BrowserContext);
  const breakpoints = useContext(BreakpointContext);

  const {
    tmntData, collectionData, sku,
  } = ProductPageState;

  const {
    hasFitInfo, isFinalSale, brand, fitInformation,
  } = collectionData?.activeProduct || {};

  const isMobile = useMemo(() => breakpoints.xxsmall && !breakpoints.medium, [breakpoints]);
  const shouldDisplayMeasurement = useMemo(() => hasFitInfo && !isMobile, [hasFitInfo, isMobile]);

  const resetATBButton = useCallback(() => {}, []);
  const { addToBagError, setAddToBagError, handleFormError } = useAddToBagErrorHandler(collectionData, tmntData, resetATBButton);

  const primarySizeTileHandler = useCallback(({ target }) => {
    handlePrimarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handlePrimarySizeUpdate, setAddToBagError]);

  const secondarySizeTileHandler = useCallback(({ target }) => {
    handleSecondarySizeUpdate(target.value);
    setAddToBagError(null);
  }, [handleSecondarySizeUpdate, setAddToBagError]);

  const preselectExchangeProduct = useMemo(
    () => collectionData?.products.find((product) => product.productId === String(preselectExchangeProductId)),
    [collectionData?.products, preselectExchangeProductId],
  );

  const preselectExchangeSwatchName = preselectExchangeProduct?.swatchName;
  const preselectCurrentSku = useMemo(
    () => collectionData?.skus.find((item) => item.shortSku === sku),
    [collectionData?.skus, sku],
  );

  const preselectExchangeSizeLabel = useMemo(() => {
    if (!preselectCurrentSku?.fullSizeLabel) return null;
    return preselectCurrentSku.fullSizeLabel.replace(/\bX\b/g, ' ');
  }, [preselectCurrentSku]);

  const handleSave = useCallback(() => {
    const isSizeSelected = hasPrimaryOrSecondarySelected(collectionData.verifiedCriteria);
    const shortSku = collectionData.qualifiedSku;

    if (shortSku && isSizeSelected) {
      const { activeProduct, verifiedCriteria } = ProductPageState.collectionData;
      const { kicId, imageSet } = activeProduct;

      const exchangeReasonData = {
        lineItemId: orderLineKeyId,
        seq: activeProduct.defaultSwatchSequence,
        collection: collectionId,
        newShortSku: collectionData.qualifiedSku,
        sizePrimary: verifiedCriteria.primarySize,
        sizeSecondary: verifiedCriteria.secondarySize,
        swatch: activeProduct.swatchName,
        productId: activeProduct.productId,
        imageId: kicId,
        imageUrl: imageSet?.prod?.[0]?.thumbnailSrc,
      };

      window.dispatchEvent(new CustomEvent('dre:updateExchangeStatus', { detail: exchangeReasonData }));

      onModalPrimaryActionSuccessCallback();
      handleCloseModal();
    } else {
      handleFormError();
    }
  }, [collectionData, ProductPageState, orderLineKeyId, collectionId, handleCloseModal, onModalPrimaryActionSuccessCallback, handleFormError]);

  if (!collectionData) return null;

  const isButtonDisabled = !collectionData.hasQualifiedSku;

  return (
    <>
      <div
        className="quickview-add-to-bag-form-wrapper exchanges-wrapper"
        data-testid="exchanges-quickview-add-to-bag-form-wrapper"
      >
        <div className="product-page-info-container-mfe">
          {isBrowser && !isMobile && (
            <ExchangesQuickviewNameAndDescription
              activeProduct={collectionData?.activeProduct}
              exchangeColorAndSize={tmntData?.exchangeColorAndSize?.value}
            />
          )}

          <div className="original-color-or-size" data-testid="exchanges-original-color-or-size">
            <h3 className="text" data-type-style="h3">
              {tmntData?.originalColorAndSize?.value}
            </h3>
            <p className="text" data-type-style="body">
              <span className="preselectSwatchName">{preselectExchangeSwatchName}</span>
              , &nbsp;
              <span>{preselectExchangeSizeLabel}</span>
            </p>
            <HorizontalLineSeparator />
            <h3 className="text" data-type-style="h3">
              {tmntData?.newColorAndSize?.value}
            </h3>
          </div>

          <ShownIn />
          <SwatchGroup attrChangeHandler={swatchChanged} siteRegion={siteRegion} />
          <SizeTileSection
            primarySizeTileHandler={primarySizeTileHandler}
            secondarySizeTileHandler={secondarySizeTileHandler}
            setSizeGuideOpen={setSizeGuideOpen}
          />

          <SizeMessaging />

          {shouldDisplayMeasurement && (
            <ModelMeasurement tmntData={tmntData} fitInformation={fitInformation} />
          )}

          {isMobile && <ContentError error={addToBagError} isDesktop={false} />}

          {isMobile && (
            <div className="product-page-info-container-mfe__add-to-bag" data-testid="product-page-info-container-mfe__add-to-bag">
              <div className="product-page-info-container-mfe__add-to-bag__wrapper">
                <Button
                  classList="product-add-to-bag-button-container_button"
                  variant="primary"
                  brand={brand}
                  isFullWidth
                  onClick={handleSave}
                  disabled={isButtonDisabled}
                >
                  {tmntData?.exchangesSave?.value}
                </Button>
              </div>
            </div>
          )}

          <div className="catalog-shop-similar__container" />

          {isFinalSale && (
            <FinalSale finalSaleMessage={tmntData?.finalSaleText?.value} />
          )}

          <ESpotContent eSpotData={ProductPageState.eSpotContent?.PrimaryPDPPromoEMS} />
          <BuyGridAccordionWrapper
            categoryId={categoryId}
            reviewData={reviewData}
            shippingContent={shippingContent}
            eSpotContent={eSpotContent}
            siteRegion={siteRegion}
          />
        </div>
      </div>

      {!isMobile && (
        <div className="add-to-bag-form-wrapper_button-section-mfe">
          <ContentError error={addToBagError} />
          <Button
            classList="product-add-to-bag-button-container_button"
            variant="primary"
            brand={brand}
            isFullWidth
            onClick={handleSave}
            disabled={isButtonDisabled}
          >
            {tmntData?.exchangesSave?.value}
          </Button>
        </div>
      )}
    </>
  );
}
